import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, KeyRound, ShieldCheck, FileCheck2, Clock, Lock, Search, Languages } from "lucide-react";
import PublicShell, { setSiteLang } from "@/components/PublicShell";

const HERO = "https://images.unsplash.com/photo-1542323228-002ac256e7b8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

const STYLE = [
  { icon: ClipboardCheck, tint: "bg-[#FFE4D6] text-[#E8573A]", bar: "bg-[#F26B4F]" },
  { icon: KeyRound, tint: "bg-[#FFF1CC] text-[#C98A00]", bar: "bg-[#FFB547]" },
  { icon: FileCheck2, tint: "bg-[#D5F5EE] text-[#0F8F7E]", bar: "bg-[#14B8A6]" },
  { icon: ShieldCheck, tint: "bg-[#E3E9F7] text-[#1B365D]", bar: "bg-[#1B365D]" },
];

const L = {
  en: {
    toggle: "Español", toggleHint: "Ver en español",
    badge: "Secure screening for churches & nonprofits",
    h1a: "Background checks for employees & volunteers,", h1b: "handled with care.",
    lead: "Info Retrieval Services helps ministries and community groups screen the people who serve alongside them — with clear steps, careful research, and privacy at every stage.",
    order: "Order screening codes", apply: "I have a screening code",
    enc: "Encrypted & access-controlled", time: "About 15 minutes to apply",
    imgAlt: "Community members joining hands", oneCode: "One code, one applicant", noReuse: "Codes can't be reused or shared.",
    already: "Already applied?", alreadySub: "Check the status of your application with your code and date of birth.",
    checkA: "Check my", checkB: "application", checkC: "status",
    how: "How it works", howTitle: "Four clear steps from order to final report",
    steps: [
      ["Organization orders codes", "Choose how many employees and volunteers you need to screen and pay securely."],
      ["We email your codes", "As soon as your payment goes through, your one-time codes are emailed to you to hand to each employee or volunteer."],
      ["Employee or volunteer applies", "Each person enters their code and completes a short, guided form."],
      ["We research & report", "Our staff completes the required searches and delivers a final report."],
    ],
  },
  es: {
    toggle: "English", toggleHint: "View in English",
    badge: "Verificación segura para iglesias y organizaciones sin fines de lucro",
    h1a: "Verificación de antecedentes para empleados y voluntarios,", h1b: "con cuidado y respeto.",
    lead: "Info Retrieval Services ayuda a ministerios y grupos comunitarios a verificar a las personas que sirven con ellos, con pasos claros, una investigación cuidadosa y privacidad en cada etapa.",
    order: "Comprar códigos", apply: "Tengo un código de verificación",
    enc: "Información cifrada y protegida", time: "Unos 15 minutos para completar la solicitud",
    imgAlt: "Miembros de la comunidad tomados de la mano", oneCode: "Un código, una persona", noReuse: "Los códigos no se pueden reutilizar ni compartir.",
    already: "¿Ya envió su solicitud?", alreadySub: "Consulte el estado de su solicitud con su código y su fecha de nacimiento.",
    checkA: "Consultar el estado", checkB: "de mi solicitud", checkC: "",
    how: "Cómo funciona", howTitle: "Cuatro pasos claros, desde el pedido hasta el informe final",
    steps: [
      ["La organización compra los códigos", "Elige cuántos empleados y voluntarios necesita verificar y paga de forma segura."],
      ["Enviamos los códigos por correo", "En cuanto se procesa el pago, la organización recibe por correo los códigos de un solo uso para entregar a cada empleado o voluntario."],
      ["El empleado o voluntario presenta su solicitud", "Cada persona ingresa su código y completa un formulario breve y guiado."],
      ["Investigamos e informamos", "Nuestro personal realiza las búsquedas requeridas y entrega un informe final."],
    ],
  },
};

function LangSwitch({ es, flip, label, hint }: { es: boolean; flip: () => void; label: string; hint: string }) {
  return (
    <button type="button" onClick={flip} aria-label={hint} data-testid="landing-lang-toggle"
      className="inline-flex items-center gap-2 rounded-full border-2 border-[#1B365D] bg-white px-4 py-2 text-sm font-bold text-[#1B365D] shadow-sm transition-colors hover:bg-[#1B365D] hover:text-white">
      <Languages className="h-4 w-4" /> {label}
      <span className="rounded-full bg-[#F3C66B] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#1B365D]">{es ? "EN" : "ES"}</span>
    </button>
  );
}

export default function Landing() {
  const [es, setEs] = useState(() => localStorage.getItem("irs_lang") === "es");
  const flip = () => { setSiteLang(es ? "en" : "es"); setEs(!es); };
  const t = es ? L.es : L.en;
  return (
    <PublicShell>
      <div className="relative overflow-hidden" lang={es ? "es" : "en"}>
      <div aria-hidden className="pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-[#FFD9C7]/60 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -left-40 top-64 h-80 w-80 rounded-full bg-[#FFE7A8]/50 blur-3xl" />
      <section className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-10 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:px-8 lg:pt-14">
        <div className="fade-up">
          <div className="flex flex-wrap items-center gap-3">
            <LangSwitch es={es} flip={flip} label={t.toggle} hint={t.toggleHint} />
            <span className="inline-flex items-center gap-2 rounded-full border border-[#F26B4F]/30 bg-[#FFE4D6] px-3.5 py-1.5 text-sm font-semibold text-[#C2410C]" data-testid="landing-badge">
              <Lock className="h-3.5 w-3.5" /> {t.badge}
            </span>
          </div>
          <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-[#1A2536] sm:text-5xl lg:text-6xl" data-testid="landing-title">
            {t.h1a} <span className="text-[#E8573A] underline decoration-[#FFB547] decoration-[6px] underline-offset-[6px]">{t.h1b}</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#475569]">{t.lead}</p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link to="/order" data-testid="landing-order-cta" className="btn-primary h-14 bg-[#F26B4F] px-7 text-[17px] shadow-[0_12px_24px_-10px_rgba(242,107,79,0.7)] hover:bg-[#E0553A]">
              {t.order} <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/apply" data-testid="landing-apply-cta" className="btn-secondary h-14 border-[#1B365D]/20 px-7 text-[17px] hover:border-[#14B8A6] hover:bg-[#F0FDFA]">
              {t.apply}
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#64748B]">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#14B8A6]" /> {t.enc}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#F26B4F]" /> {t.time}</span>
          </div>
        </div>
        <div className="fade-up relative [animation-delay:120ms]">
          <div className="absolute -left-6 -top-6 h-40 w-40 rounded-[2.5rem] bg-[#FFB547]/50 blur-2xl" />
          <div className="absolute -bottom-8 -right-6 h-44 w-44 rounded-full bg-[#F26B4F]/30 blur-2xl" />
          <img src={HERO} alt={t.imgAlt} className="relative h-[420px] w-full rounded-[2rem] object-cover shadow-[0_30px_60px_-20px_rgba(27,54,93,0.35)] lg:h-[520px]" />
          <div className="card-soft absolute -bottom-6 left-6 right-6 flex items-center gap-4 p-5 sm:left-auto sm:right-8 sm:w-80">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#DCFCE7] text-[#15803D]"><FileCheck2 className="h-6 w-6" /></span>
            <div>
              <p className="font-heading text-sm font-bold text-[#1A2536]">{t.oneCode}</p>
              <p className="text-sm text-[#64748B]">{t.noReuse}</p>
            </div>
          </div>
        </div>
      </section>
      </div>

      <section className="mx-auto max-w-7xl px-4 pb-16 pt-6 sm:px-6 lg:px-8" lang={es ? "es" : "en"}>
        <Link to={es ? "/check-status?lang=es" : "/check-status"} data-testid="landing-status-banner" className="group relative flex flex-col gap-6 overflow-hidden rounded-[2rem] bg-[#1B365D] px-7 py-9 text-white shadow-[0_30px_60px_-24px_rgba(27,54,93,0.6)] transition-transform duration-300 hover:-translate-y-0.5 sm:flex-row sm:items-center sm:justify-between sm:px-12 sm:py-11">
          <span aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[#2A4A7A]" />
          <span aria-hidden className="pointer-events-none absolute right-24 top-8 hidden h-28 w-28 rounded-full border-2 border-[#E8B04A]/30 sm:block" />
          <span aria-hidden className="absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r from-[#F3C66B] to-[#D99A2B]" />
          <span className="relative flex items-center gap-5">
            <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-white/10 text-[#F3C66B] ring-1 ring-white/20"><Search className="h-8 w-8" /></span>
            <span>
              <span className="block font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">{t.already}</span>
              <span className="mt-1 block text-base text-white/80 sm:text-lg">{t.alreadySub}</span>
            </span>
          </span>
          <span className="relative inline-flex h-16 items-center justify-center gap-3 whitespace-nowrap rounded-2xl bg-[#F3C66B] px-6 text-lg font-bold sm:px-8 text-[#1B365D] shadow-lg transition-colors group-hover:bg-white" data-testid="landing-status-banner-button">
            {t.checkA} <span className="hidden sm:inline">{t.checkB}</span> {t.checkC} <ArrowRight className="h-6 w-6 transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      </section>

      <section className="bg-[#FFF6EE] py-16" lang={es ? "es" : "en"}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#E8573A]">{t.how}</p>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold tracking-tight text-[#1A2536] sm:text-3xl">{t.howTitle}</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STYLE.map((s, i) => (
            <div key={i} className="card-soft fade-up relative overflow-hidden p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg" style={{ animationDelay: `${i * 90}ms` }} data-testid={`landing-step-${i}`}>
              <span className={`absolute inset-x-0 top-0 h-1.5 ${s.bar}`} />
              <div className="flex items-center justify-between">
                <span className={`grid h-12 w-12 place-items-center rounded-2xl ${s.tint}`}><s.icon className="h-6 w-6" /></span>
                <span className="font-mono text-sm font-semibold text-[#F4A48F]">0{i + 1}</span>
              </div>
              <h3 className="mt-6 text-lg font-bold text-[#1A2536]">{t.steps[i][0]}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[#64748B]">{t.steps[i][1]}</p>
            </div>
          ))}
        </div>
      </div>
      </section>
    </PublicShell>
  );
}
